import { useMemo } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { mergeArchivePages, type MergedArchivePages } from '../archiveMerge';
import { ARCHIVE_PAGE_LIMIT, POLL_MS } from '../config';
import { getArchiveCalls } from '../endpoints';
import { queryKeys } from '../queryKeys';
import type { ArchiveCallFilters } from '../types';
import { queryRetry } from './shared';

export interface UseArchiveCallsOptions {
  readonly enabled?: boolean;
  // Polling interval for page 1; false disables (api-surface: the desk polls 10 s).
  readonly pollMs?: number | false;
}

export interface ArchiveCallsResult extends MergedArchivePages {
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  // True while rows from the previous filter set are shown as a placeholder
  // during the first fetch of a new filter set; label them, do not trust them.
  readonly isPlaceholder: boolean;
  // True when rows are on screen but the latest refresh failed: the rows stay
  // and the surface labels them Stale with the error as the reason.
  readonly stale: boolean;
  readonly error: unknown;
  readonly errorUpdatedAt: number;
  readonly dataUpdatedAt: number;
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
}

const EMPTY: MergedArchivePages = {
  rows: [],
  duplicatesDropped: 0,
  coverage: null,
  ambiguity: null,
  truncated: false,
  nextCursor: null,
};

// Cursor pagination over GET /api/beta/evidence-archive/calls. The filter set
// is in the key, so a filter change starts a fresh pagination (the server
// cursor is filter fingerprinted). Rows are deduplicated by id across pages.
export function useArchiveCalls(filters: ArchiveCallFilters = {}, options: UseArchiveCallsOptions = {}): ArchiveCallsResult {
  const effectiveFilters: ArchiveCallFilters = { limit: ARCHIVE_PAGE_LIMIT.default, ...filters };
  const pollMs = options.pollMs === undefined ? POLL_MS.archiveCalls : options.pollMs;

  const query = useInfiniteQuery({
    queryKey: queryKeys.archiveCalls(effectiveFilters),
    queryFn: ({ pageParam, signal }) => getArchiveCalls({
      filters: effectiveFilters,
      signal,
      ...(pageParam ? { cursor: pageParam } : {}),
    }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: options.enabled ?? true,
    refetchInterval: pollMs,
    refetchIntervalInBackground: false,
    placeholderData: keepPreviousData,
    retry: queryRetry,
  });

  const pages = query.data?.pages;
  const merged = useMemo(() => (pages ? mergeArchivePages(pages) : EMPTY), [pages]);

  return {
    ...merged,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    isPlaceholder: query.isPlaceholderData,
    stale: query.isError && merged.rows.length > 0,
    error: query.error,
    errorUpdatedAt: query.errorUpdatedAt,
    dataUpdatedAt: query.dataUpdatedAt,
    fetchNextPage: () => void query.fetchNextPage(),
    refetch: () => void query.refetch(),
  };
}
